import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Flame, Loader2, Upload, X, Package, Zap, Check, ArrowRight, Truck, ShirtIcon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { LeadsAPI, apiErrorMessage } from "@/lib/api";

const SERVICE_FALLBACK = [
  { id: "dtf_transfer",    label: "DTF Transfer (each)",        unit_price: 8.0 },
  { id: "bulk_dtf",        label: "Bulk DTF run (50+)",         unit_price: 8.0 },
  { id: "custom_halftone", label: "Custom halftone conversion", unit_price: 25.0 },
  { id: "vector_redraw",   label: "Logo / vector redraw",       unit_price: 45.0 },
  { id: "photo_retouch",   label: "Photo retouch (per image)",  unit_price: 15.0 },
  { id: "rush_job",        label: "Rush job / same-day",        unit_price: 0.0 },
  { id: "other",           label: "Other / custom",             unit_price: 0.0 },
];

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ServicesPage() {
  const { toast } = useToast();
  const [services, setServices] = React.useState(SERVICE_FALLBACK);
  const [form, setForm] = React.useState({
    name: "", email: "", phone: "", business: "",
    service: "dtf_transfer", quantity: "", deadline: "", budget: "", message: "",
  });
  const [files, setFiles] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await LeadsAPI.services();
        if (r.data?.services?.length) setServices(r.data.services);
      } catch {
        // Silent: keep fallback list.
      }
    })();
  }, []);

  const selected = services.find((s) => s.id === form.service) || services[0];
  const qtyNum = parseInt(form.quantity, 10) || 0;
  const rough =
    selected && selected.unit_price > 0 && qtyNum > 0
      ? selected.unit_price * qtyNum
      : null;

  const onFiles = async (list) => {
    const arr = Array.from(list || []).slice(0, 5 - files.length);
    const enriched = [];
    for (const f of arr) {
      if (f.size > 4 * 1024 * 1024) {
        toast({ title: `${f.name} too large`, description: "Max 4 MB per file", variant: "destructive" });
        continue;
      }
      const b64 = await fileToBase64(f);
      enriched.push({ filename: f.name, mime: f.type || "image/png", data_base64: b64 });
    }
    setFiles((f) => [...f, ...enriched]);
  };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.service) {
      toast({ title: "Fill required fields", description: "Name, email, service and details are required.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone || undefined,
        business: form.business || undefined,
        service: form.service,
        quantity: qtyNum || undefined,
        deadline: form.deadline || undefined,
        budget: form.budget || undefined,
        message: form.message.trim(),
        attachments: files.length ? files : undefined,
      };
      await LeadsAPI.create(payload);
      setDone(true);
      toast({ title: "Quote request received", description: "We'll email you within one business day." });
    } catch (err) {
      toast({ title: "Couldn't submit", description: apiErrorMessage(err), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="grain">
        <Navbar />
        <section className="mx-auto max-w-2xl px-4 py-24 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#4aa4ff]/15 text-[#4aa4ff]">
            <Check className="h-7 w-7" />
          </div>
          <h1 className="display-font mt-6 text-3xl font-bold sm:text-4xl">Request received.</h1>
          <p className="mt-3 text-muted-foreground">
            Thanks for reaching out. We&rsquo;ll reply to <span className="text-foreground">{form.email}</span> within one business day with a quote and next steps.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <a href="/"><Button variant="outline">Back to home</Button></a>
            <a href="/studio/halftone"><Button className="btn-ember font-semibold">Open the Studio</Button></a>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="grain">
      <Navbar />
      <section className="relative py-16">
        <div className="forge-glow absolute inset-0" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="chip"><Package className="h-3 w-3 text-[#4aa4ff]" /> Done-for-you</span>
              <h1 className="display-font mt-3 text-4xl font-bold sm:text-5xl">
                DTF transfers, printed and shipped.
              </h1>
              <p className="mt-3 max-w-2xl text-muted-foreground">
                Send us the art — we run press-ready halftones, print gang sheets, and drop the transfers at your door.
                <span className="ml-2 rounded-md bg-[#4aa4ff]/15 px-2 py-0.5 text-xs font-semibold text-[#4aa4ff]">
                  $8 / transfer
                </span>
              </p>
            </div>
            <a href="#quote"><Button className="btn-ember h-11 gap-2 font-semibold">Request a quote <ArrowRight className="h-4 w-4" /></Button></a>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            <FeatureCard icon={ShirtIcon} title="Any garment" body="Cotton, poly-blend, tri-blend, hoodies, tote bags. We match the pattern to your fabric." />
            <FeatureCard icon={Zap}       title="72-hour turnaround" body="Small runs ship in 3 business days. Rush service available on request." />
            <FeatureCard icon={Truck}     title="Flat-rate shipping" body="USPS Priority tracked. We combine multiple orders to save you money." />
          </div>

          <div id="quote" className="mt-14 grid gap-8 lg:grid-cols-[1.15fr_1fr]">
            <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 sm:p-8">
              <h2 className="display-font text-2xl font-bold">Request a quote</h2>
              <p className="mt-1 text-sm text-muted-foreground">Fields marked * are required. We usually reply within a few hours during business days.</p>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" data-testid="lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 h-11" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" data-testid="lead-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 h-11" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" data-testid="lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11" />
                </div>
                <div>
                  <Label htmlFor="business">Business</Label>
                  <Input id="business" data-testid="lead-business" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} className="mt-1 h-11" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Service *</Label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger data-testid="lead-service" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.label}{s.unit_price > 0 ? ` · $${s.unit_price.toFixed(2)}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="quantity">Quantity</Label>
                  <Input id="quantity" data-testid="lead-quantity" type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1 h-11" />
                </div>
                <div>
                  <Label htmlFor="deadline">Deadline</Label>
                  <Input id="deadline" data-testid="lead-deadline" placeholder="e.g. Aug 15 or ASAP" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1 h-11" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="budget">Budget (optional)</Label>
                  <Input id="budget" data-testid="lead-budget" placeholder="e.g. $200-$400" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-1 h-11" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message">Project details *</Label>
                  <Textarea id="message" data-testid="lead-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required className="mt-1" placeholder="Garment type + color, art files, sizes needed, anything else we should know." />
                </div>

                <div className="sm:col-span-2">
                  <Label>Attach artwork (optional — up to 5 images)</Label>
                  <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-background p-4 text-sm text-muted-foreground hover:border-[#4aa4ff]/60">
                    <Upload className="h-4 w-4" />
                    <span>Click or drop images here (PNG/JPG, up to 4 MB each)</span>
                    <input type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} data-testid="lead-file-input" />
                  </label>
                  {files.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
                      {files.map((f, i) => (
                        <div key={i} className="relative rounded-md border border-border bg-background p-1">
                          <img src={`data:${f.mime};base64,${f.data_base64}`} alt={f.filename} className="h-20 w-full rounded object-contain" />
                          <button type="button" onClick={() => removeFile(i)} className="absolute -right-1 -top-1 rounded-full bg-black/80 p-0.5 text-white">
                            <X className="h-3 w-3" />
                          </button>
                          <div className="mono truncate text-[9px] text-muted-foreground">{f.filename}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button type="submit" data-testid="lead-submit-btn" disabled={busy} className="btn-ember h-11 gap-2 font-semibold">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flame className="h-4 w-4" />} Request quote
                </Button>
                <div className="text-xs text-muted-foreground">By submitting you agree to be contacted about your project.</div>
              </div>
            </form>

            <aside className="space-y-4">
              <div className="rounded-2xl chrome-border p-6">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Order summary</div>
                <div className="mt-2 text-sm text-muted-foreground">Selected service</div>
                <div className="display-font text-lg font-bold">{selected?.label || "—"}</div>
                {selected?.unit_price > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">Starting at <span className="text-foreground font-semibold">${selected.unit_price.toFixed(2)}</span> per unit</div>
                )}
                {rough && (
                  <div className="mt-4 rounded-lg border border-border bg-background p-3">
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Rough estimate</div>
                    <div className="display-font mt-1 text-3xl font-bold">${rough.toFixed(2)}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {qtyNum} × ${selected.unit_price.toFixed(2)} — final quote emailed after we review your art.
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">How it works</div>
                <ol className="mt-3 space-y-3 text-sm text-muted-foreground">
                  <li><span className="text-foreground font-semibold">1.</span> Send us your art + garment info.</li>
                  <li><span className="text-foreground font-semibold">2.</span> We reply with a firm quote and mockup.</li>
                  <li><span className="text-foreground font-semibold">3.</span> Approve and pay — we press &amp; ship within 3 days.</li>
                </ol>
              </div>

              <div className="rounded-2xl border border-[#4aa4ff]/40 bg-gradient-to-br from-[#0a1024] to-card p-6">
                <div className="display-font text-sm font-bold">Prefer DIY?</div>
                <p className="mt-1 text-xs text-muted-foreground">Use our browser Studio — 12 halftone styles, DTF-ready exports at 300 DPI, free tier included.</p>
                <a href="/studio/halftone" className="mt-3 inline-flex items-center gap-1 text-xs text-[#4aa4ff] hover:underline">Open the Studio <ArrowRight className="h-3 w-3" /></a>
              </div>
            </aside>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-[#4aa4ff]">
        <Icon className="h-5 w-5" />
      </div>
      <div className="display-font text-base font-bold">{title}</div>
      <div className="mt-1 text-sm text-muted-foreground">{body}</div>
    </div>
  );
}
